import React from 'react';
import { ProductOptionInput, ProductVariantInput, ProductVariantOptionInput } from '@shopify-multiseller/graphql/partner/types';
import { Modal } from '..';

export interface IVariantSelectionModalProps {
  open: boolean;
  onClose: () => void;
  options: ProductOptionInput[];
  existingVariants: ProductVariantInput[];
  onSelect: (_variantOptions: ProductVariantOptionInput[]) => void;
}

export const VariantSelectionModal: React.FC<IVariantSelectionModalProps> = ({
  open,
  onClose,
  options,
  existingVariants,
  onSelect,
}) => {
  // Generate all possible combinations of options
  const generateVariantCombinations = () => {
    const allCombinations: ProductVariantOptionInput[][] = [];
    
    const generateCombinations = (
      currentCombination: ProductVariantOptionInput[] = [],
      optionIndex = 0
    ) => {
      if (optionIndex === options.length) {
        allCombinations.push(currentCombination);
        return;
      }

      const currentOption = options[optionIndex];
      currentOption.values.forEach(value => {
        generateCombinations([
          ...currentCombination,
          { optionId: currentOption.id, value }
        ], optionIndex + 1);
      });
    };

    generateCombinations();
    return allCombinations;
  };

  // Check if a combination already exists in the variants. The option sets
  // must be the same size — a variant carrying only a subset of the options
  // (e.g. created before a second option was added) must not block every
  // combination that happens to contain its values.
  const isVariantExists = (combination: ProductVariantOptionInput[]) => {
    return existingVariants.some(variant => {
      if (!variant.variantOptions) return false;
      if (variant.variantOptions.length !== combination.length) return false;
      return variant.variantOptions.every(vo => {
        const matchingOption = combination.find(co => co.optionId === vo.optionId);
        return matchingOption && matchingOption.value === vo.value;
      });
    });
  };

  // Format the variant options for display
  const formatVariantOptions = (combination: ProductVariantOptionInput[]) => {
    return combination
      .map(co => co.value)
      .join(' / ');
  };

  const allCombinations = generateVariantCombinations();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Variant"
      footer={true}
      size="small"
      secondaryActions={{
        label: 'Cancel',
        variant: 'outline',
        onClick: onClose,
      }}
    >
      <div className="space-y-1">
        {allCombinations.map((combination) => {
          const exists = isVariantExists(combination);
          const label = formatVariantOptions(combination);
          return (
            <button
              key={combination.map((co) => `${co.optionId}:${co.value}`).join('|')}
              data-testid="variant-combination-option"
              className={`w-full text-left p-3 border rounded-md ${
                exists
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'hover:bg-primary/10 border-border'
              }`}
              onClick={() => {
                if (!exists) {
                  onSelect(combination);
                  onClose();
                }
              }}
              disabled={exists}
            >
              {label}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}; 